import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, User, Phone, Car, Star, Edit2, CheckCircle, Camera } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const DriverProfile: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, updateProfile } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(currentUser?.name || '');
  const [isSaving, setIsSaving] = useState(false);

  const driver = currentUser as any;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ name });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/driver/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">الملف الشخصي</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col items-center">
            {/* Photo */}
            <div className="relative mb-4">
              <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center">
                {driver?.photoURL ? (
                  <img 
                    src={driver.photoURL} 
                    alt={driver.name} 
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-secondary-600" />
                )}
              </div>
              <button className="absolute bottom-0 left-0 w-8 h-8 bg-secondary-500 text-white rounded-full flex items-center justify-center shadow-lg">
                <Camera className="w-4 h-4" />
              </button>
            </div>

            {/* Name */}
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-xl font-bold text-center border-b-2 border-secondary-500 focus:outline-none mb-2"
              />
            ) : (
              <h2 className="text-xl font-bold mb-1">{driver?.name}</h2>
            )}
            
            <p className="text-gray-500" dir="ltr">{driver?.phone}</p>

            {/* Rating */}
            <div className="flex items-center gap-2 mt-3">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="font-bold">{driver?.rating || '5.0'}</span>
              <span className="text-gray-500">({driver?.totalRides || 0} رحلة)</span>
            </div>

            {/* Verification Badge */}
            {driver?.isVerified ? (
              <span className="mt-3 px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                موثق
              </span>
            ) : (
              <span className="mt-3 px-4 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                بانتظار التحقق
              </span>
            )}
          </div>
        </div>

        {/* Personal Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">المعلومات الشخصية</h3>
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              className="text-secondary-600 flex items-center gap-1"
            >
              {isEditing ? (
                <>{isSaving ? 'جاري الحفظ...' : <><CheckCircle className="w-4 h-4" /> حفظ</>}</>
              ) : (
                <><Edit2 className="w-4 h-4" /> تعديل</>
              )}
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">رقم الهاتف</p>
                <p className="font-medium" dir="ltr">{driver?.phone}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h3 className="font-bold text-lg mb-4">معلومات المركبة</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Car className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">السيارة</p>
                <p className="font-medium">
                  {driver?.vehicle?.make} {driver?.vehicle?.model} ({driver?.vehicle?.year})
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <span className="text-gray-500 font-bold">#</span>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">رقم اللوحة</p>
                <p className="font-medium" dir="ltr">
                  {driver?.vehicle?.plateNumber} - {driver?.vehicle?.plateCity}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-lg mb-4">إحصائياتي</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-secondary-50 rounded-xl p-4 text-center">
              <Car className="w-8 h-8 text-secondary-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-secondary-600">{driver?.totalRides || 0}</p>
              <p className="text-sm text-gray-600">رحلة منجزة</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-600">{driver?.rating || '5.0'}</p>
              <p className="text-sm text-gray-600">تقييمي</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DriverProfile;
